import { Injectable, OnDestroy } from '@angular/core';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { WsIncomingMessage, WsUserMessage } from '../models/websocket-message.model';
import { environment } from '../../../environments/environment';

export type ConnectionState = 'connected' | 'disconnected' | 'reconnecting';

type Milliseconds = number;

@Injectable({ providedIn: 'root' })
export class WebSocketService implements OnDestroy {
  private socket: WebSocket | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly messagesSubject = new Subject<WsIncomingMessage>();
  private readonly connectionStateSubject = new BehaviorSubject<ConnectionState>('disconnected');

  private reconnectAttempts = 0;
  private readonly MAX_RECONNECT_ATTEMPTS = 5;
  private readonly INITIAL_BACKOFF_MS: Milliseconds = 1000;
  private readonly MAX_BACKOFF_MS: Milliseconds = 30000;
  private readonly BACKOFF_FACTOR = 2;
  private readonly HEARTBEAT_INTERVAL_MS: Milliseconds = 30000;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private currentUrl: string | null = null;
  private currentToken: string | null = null;

  readonly messages$: Observable<WsIncomingMessage> = this.messagesSubject.asObservable();
  readonly connectionState$: Observable<ConnectionState> = this.connectionStateSubject.asObservable();

  connect(url: string, token: string): void {
    // Normalize the WebSocket URL to connect through the BFF proxy.
    // The downstream service may return an absolute URL (e.g., ws://localhost:3000/ws/...)
    // but the client should connect via its own origin so the Angular dev proxy
    // or production reverse proxy routes it to the BFF's WS gateway.
    const wsUrl = this.normalizeWsUrl(url);

    // If already connected/connecting to the same URL with the same token, do nothing.
    // Otherwise, tear down any existing socket before opening a new one.
    if (this.socket && this.currentUrl === wsUrl && this.currentToken === (token || null)) {
      const state = this.socket.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        return;
      }
    }

    this.disconnect();
    this.currentUrl = wsUrl;
    this.currentToken = token || null;
    this.reconnectAttempts = 0;
    this.establishConnection();
  }

  send(message: WsUserMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  disconnect(): void {
    this.stopHeartbeat();
    this.clearReconnectTimer();
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      this.socket.onopen = null;
      this.socket.close(1000, 'Client disconnect');
      this.socket = null;
    }
    this.connectionStateSubject.next('disconnected');
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
    this.messagesSubject.complete();
    this.connectionStateSubject.complete();
  }

  private establishConnection(): void {
    if (!this.currentUrl) {
      return;
    }

    const wsUrl = this.currentToken
      ? `${this.currentUrl}?token=${this.currentToken}`
      : this.currentUrl;
    this.socket = new WebSocket(wsUrl);

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.connectionStateSubject.next('connected');
      this.startHeartbeat();
    };

    this.socket.onmessage = (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data as string) as WsIncomingMessage;
        this.messagesSubject.next(parsed);
      } catch {
        // Ignore malformed messages
      }
    };

    this.socket.onclose = (event: CloseEvent) => {
      this.stopHeartbeat();
      if (event.code !== 1000) {
        this.attemptReconnect();
      } else {
        this.connectionStateSubject.next('disconnected');
      }
    };

    this.socket.onerror = () => {
      // The close event will fire after error, triggering reconnect logic
      this.socket?.close();
    };
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
      this.connectionStateSubject.next('disconnected');
      return;
    }

    this.connectionStateSubject.next('reconnecting');

    const backoff = Math.min(
      this.INITIAL_BACKOFF_MS * Math.pow(this.BACKOFF_FACTOR, this.reconnectAttempts),
      this.MAX_BACKOFF_MS
    );
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => this.establishConnection(), backoff);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: 'ping' }));
      }
    }, this.HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private normalizeWsUrl(url: string): string {
    try {
      const parsed = new URL(url);
      // Use the configured wsUrl base and append the pathname from the downstream URL
      return `${environment.wsUrl}${parsed.pathname}`;
    } catch {
      // If it's already a relative path, prepend the configured wsUrl base
      return `${environment.wsUrl}${url}`;
    }
  }
}
